import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import {
  colors,
  gradients,
  shadows,
  typography,
  borderRadius,
  getCosmicBackground,
  getCardStyle,
  getButtonStyle,
  getStatusBadgeStyle,
} from '../theme';

interface User {
  id: number;
  email: string;
  subscription_status: string;
  trial_end_date?: string;
  role: string;
  created_at: string;
}

interface UsageStats {
  calculations_used: number;
  free_limit: number;
  remaining_calculations: number;
  subscription_status: string;
}

interface SubscriptionData {
  subscription_status: string;
  trial_end_date?: string;
  subscription_details?: {
    status?: string;
    cancel_at_period_end?: boolean;
    current_period_end?: string;
  };
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const fetchUserData = async () => {
    try {
      // Sync subscription status with Stripe first (don't fail if this errors)
      try {
        await fetch(`${API_URL}/subscriptions/verify-and-sync`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (syncErr) {
        console.error('Sync failed:', syncErr);
      }

      const userRes = await fetch(`${API_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Only logout on 401 Unauthorized, not other errors
      if (userRes.status === 401) {
        localStorage.removeItem('auth_token');
        navigate('/login');
        return;
      }

      if (userRes.ok) {
        const userData = await userRes.json();
        setUser(userData);
      }

      // These are optional - don't fail the page if they error
      try {
        const usageRes = await fetch(`${API_URL}/users/usage`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (usageRes.ok) {
          const usageData = await usageRes.json();
          setUsage(usageData);
        }
      } catch (usageErr) {
        console.error('Failed to fetch usage:', usageErr);
      }

      try {
        const subRes = await fetch(`${API_URL}/subscriptions/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (subRes.ok) {
          const subData = await subRes.json();
          setSubscription(subData.data);
        }
      } catch (subErr) {
        console.error('Failed to fetch subscription:', subErr);
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      // Don't logout on general errors, just log them
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    const isTrial = subscription?.subscription_details?.status === 'trialing' ||
      subscription?.subscription_status === 'trial' ||
      (user?.trial_end_date && new Date(user.trial_end_date) > new Date());

    const message = isTrial
      ? 'Are you sure you want to cancel your trial? Your premium access will end immediately.'
      : 'Are you sure you want to cancel your subscription? Your premium access will end immediately.';

    if (!window.confirm(message)) {
      return;
    }

    setActionLoading(true);
    setActionMessage(null);

    try {
      const response = await fetch(`${API_URL}/subscriptions/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to cancel subscription');
      }

      const successMsg = isTrial
        ? 'Trial cancelled. You are now on the free plan.'
        : 'Subscription cancelled. You are now on the free plan.';
      setActionMessage({ type: 'success', text: successMsg });

      // Clear subscription state since it's been cancelled
      setSubscription(null);

      // Also try to refresh data from server
      fetchUserData();
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{
        ...getCosmicBackground(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.text.starWhite,
        fontFamily: typography.fontFamily.heading,
        fontSize: typography.fontSize.xl,
        letterSpacing: typography.letterSpacing.wider,
      }}>
        <span style={{ color: colors.accent.gold, marginRight: '12px' }}>★</span>
        Loading...
      </div>
    );
  }

  return (
    <div style={{
      ...getCosmicBackground(),
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Star pattern background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(2px 2px at 20px 30px, rgba(212, 175, 55, 0.3), transparent),
          radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.4), transparent),
          radial-gradient(2px 2px at 130px 80px, rgba(157, 143, 219, 0.2), transparent)
        `,
        backgroundSize: '200px 200px',
        pointerEvents: 'none',
        opacity: 0.5,
        zIndex: 0,
      }} />

      {/* Header */}
      <div style={{ maxWidth: '600px', margin: '0 auto 30px', position: 'relative', zIndex: 1 }}>
        <Link to="/dashboard" style={{
          color: colors.text.secondary,
          textDecoration: 'none',
          fontSize: typography.fontSize.sm,
        }}>
          ← Back to Dashboard
        </Link>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Profile Header */}
        <div style={{ ...getCardStyle('default'), textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '100px',
            height: '100px',
            background: gradients.primaryButton,
            borderRadius: '50%',
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '48px',
            boxShadow: shadows.violetGlow,
            color: colors.text.starWhite,
          }}>
            ☆
          </div>
          <h1 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.text.starWhite,
            margin: '0 0 12px 0',
            letterSpacing: typography.letterSpacing.wide,
          }}>{user?.email}</h1>
          <span style={getStatusBadgeStyle(user?.subscription_status as 'premium' | 'trial' | 'free' || 'free')}>
            {user?.subscription_status?.toUpperCase()} ACCOUNT
          </span>
        </div>

        {/* Account Details */}
        <div style={{ ...getCardStyle('default'), marginBottom: '24px' }}>
          <h2 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize.xl,
            color: colors.accent.gold,
            margin: '0 0 24px 0',
            letterSpacing: typography.letterSpacing.wide,
          }}>Account Details</h2>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: typography.fontSize.xs,
              color: colors.text.muted,
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.wider,
            }}>
              Email Address
            </label>
            <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.text.starWhite }}>{user?.email}</p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: typography.fontSize.xs,
              color: colors.text.muted,
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.wider,
            }}>
              Member Since
            </label>
            <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.text.starWhite }}>
              {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: typography.fontSize.xs,
              color: colors.text.muted,
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.wider,
            }}>
              Subscription Status
            </label>
            <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.text.starWhite, textTransform: 'capitalize' }}>
              {user?.subscription_status}
              {user?.subscription_status === 'trial' && user?.trial_end_date && (
                <span style={{ fontWeight: typography.fontWeight.normal, color: colors.text.muted }}>
                  {' '}(expires {new Date(user.trial_end_date).toLocaleDateString()})
                </span>
              )}
            </p>
          </div>

          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <div>
              <label style={{
                display: 'block',
                fontSize: typography.fontSize.xs,
                color: colors.text.muted,
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wider,
              }}>
                Role
              </label>
              <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.accent.gold, textTransform: 'capitalize' }}>
                {user.role.replace('_', ' ')}
              </p>
            </div>
          )}
        </div>

        {/* Usage Stats */}
        {usage && (() => {
          // Detect if user has unlimited access (premium or trial)
          const rawStripeStatus = subscription?.subscription_details?.status;
          const userStatus = subscription?.subscription_status || user?.subscription_status;
          const hasUnlimited = userStatus === 'premium' || userStatus === 'trial' ||
            rawStripeStatus === 'active' || rawStripeStatus === 'trialing' ||
            (user?.trial_end_date && new Date(user.trial_end_date) > new Date());

          return (
            <div style={{ ...getCardStyle('default'), marginBottom: '24px' }}>
              <h2 style={{
                fontFamily: typography.fontFamily.heading,
                fontSize: typography.fontSize.xl,
                color: colors.accent.gold,
                margin: '0 0 24px 0',
                letterSpacing: typography.letterSpacing.wide,
              }}>This Month's Usage</h2>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ color: colors.text.secondary }}>Calculations Used</span>
                  <span style={{ fontWeight: typography.fontWeight.semibold, color: colors.text.starWhite }}>
                    {usage.calculations_used} / {hasUnlimited ? '\u221E' : usage.free_limit}
                  </span>
                </div>
                {!hasUnlimited && (
                  <div style={{
                    background: 'rgba(74, 63, 138, 0.3)',
                    borderRadius: borderRadius.full,
                    height: '10px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      background: gradients.goldButton,
                      height: '100%',
                      width: `${Math.min((usage.calculations_used / usage.free_limit) * 100, 100)}%`,
                      borderRadius: borderRadius.full,
                      boxShadow: shadows.goldGlow,
                    }} />
                  </div>
                )}
              </div>

              {!hasUnlimited && (
                <p style={{ color: colors.text.secondary, fontSize: typography.fontSize.sm, margin: 0 }}>
                  {usage.remaining_calculations > 0
                    ? `You have ${usage.remaining_calculations} calculation(s) remaining this month.`
                    : 'You\'ve used all your free calculations this month.'}
                </p>
              )}

              {hasUnlimited && (
                <p style={{ color: colors.status.success, fontSize: typography.fontSize.sm, margin: 0 }}>
                  ★ Unlimited calculations with your premium access!
                </p>
              )}
            </div>
          );
        })()}

        {/* Subscription Actions */}
        {(() => {
          // Detect subscription status from multiple sources
          const rawStripeStatus = subscription?.subscription_details?.status;
          const userStatus = subscription?.subscription_status || user?.subscription_status;
          const isPremium = userStatus === 'premium' || rawStripeStatus === 'active';
          const isTrial = userStatus === 'trial' ||
            rawStripeStatus === 'trialing' ||
            (user?.trial_end_date && new Date(user.trial_end_date) > new Date());
          const hasPremiumAccess = isPremium || isTrial;
          const isCancelled = subscription?.subscription_details?.cancel_at_period_end;

          return (
            <div style={{ ...getCardStyle('default'), marginBottom: '24px' }}>
              <h2 style={{
                fontFamily: typography.fontFamily.heading,
                fontSize: typography.fontSize.xl,
                color: colors.accent.gold,
                margin: '0 0 24px 0',
                letterSpacing: typography.letterSpacing.wide,
              }}>Subscription</h2>

              {actionMessage && (
                <div style={{
                  padding: '12px 16px',
                  marginBottom: '16px',
                  borderRadius: borderRadius.md,
                  background: actionMessage.type === 'success' ? colors.status.successBg : colors.status.errorBg,
                  color: actionMessage.type === 'success' ? colors.status.success : colors.status.error,
                  border: `1px solid ${actionMessage.type === 'success' ? 'rgba(74, 222, 128, 0.3)' : 'rgba(248, 113, 113, 0.3)'}`,
                }}>
                  {actionMessage.text}
                </div>
              )}

              {!hasPremiumAccess && (
                <div>
                  <p style={{ color: colors.text.secondary, marginBottom: '20px' }}>
                    Upgrade to Premium for unlimited calculations and access to all decision types.
                  </p>
                  <Link to="/subscription" style={{ textDecoration: 'none' }}>
                    <button style={{
                      ...getButtonStyle('gold'),
                      width: '100%',
                    }}>
                      ★ Upgrade to Premium - $3.99/month
                    </button>
                  </Link>
                </div>
              )}

              {hasPremiumAccess && (
                <div>
                  {isTrial ? (
                    <p style={{ color: colors.status.warning, marginBottom: '16px' }}>
                      ★ Premium Trial Active
                      {user?.trial_end_date && (
                        <span style={{ display: 'block', fontSize: typography.fontSize.sm, marginTop: '4px' }}>
                          Ends on {new Date(user.trial_end_date).toLocaleDateString()} - then $3.99/month
                        </span>
                      )}
                    </p>
                  ) : (
                    <p style={{ color: colors.status.success, marginBottom: '16px' }}>
                      ★ Premium Subscription Active
                      {subscription?.subscription_details?.current_period_end && (
                        <span style={{ display: 'block', fontSize: typography.fontSize.sm, marginTop: '4px' }}>
                          Next billing: {new Date(subscription.subscription_details.current_period_end).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  )}

                  {isCancelled ? (
                    <div style={{
                      padding: '14px',
                      background: colors.status.warningBg,
                      borderRadius: borderRadius.md,
                      textAlign: 'center',
                      color: colors.status.warning,
                    }}>
                      {isTrial
                        ? 'Your trial will end and you will not be charged.'
                        : 'Your subscription is set to cancel at the end of the billing period.'}
                    </div>
                  ) : (
                    <button
                      onClick={handleCancelSubscription}
                      disabled={actionLoading}
                      style={{
                        width: '100%',
                        background: 'transparent',
                        color: colors.status.error,
                        border: `1px solid ${colors.status.error}`,
                        padding: '14px',
                        borderRadius: borderRadius.md,
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        opacity: actionLoading ? 0.7 : 1,
                        fontSize: typography.fontSize.base,
                        fontFamily: typography.fontFamily.body,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {actionLoading ? 'Processing...' : isTrial ? 'Cancel Trial' : 'Cancel Subscription'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Quick Links */}
        <div style={{ ...getCardStyle('default'), marginBottom: '24px' }}>
          <h2 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize.xl,
            color: colors.accent.gold,
            margin: '0 0 24px 0',
            letterSpacing: typography.letterSpacing.wide,
          }}>Quick Links</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Link to="/birth-data" style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px 20px',
              background: 'rgba(74, 63, 138, 0.15)',
              borderRadius: borderRadius.md,
              textDecoration: 'none',
              color: colors.text.starWhite,
              border: `1px solid ${colors.border.subtle}`,
              transition: 'all 0.3s ease',
            }}>
              <span style={{ marginRight: '16px', fontSize: '24px', color: colors.accent.gold }}>☼</span>
              <span>View Birth Date</span>
            </Link>
            <Link to="/calculate" style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px 20px',
              background: 'rgba(74, 63, 138, 0.15)',
              borderRadius: borderRadius.md,
              textDecoration: 'none',
              color: colors.text.starWhite,
              border: `1px solid ${colors.border.subtle}`,
              transition: 'all 0.3s ease',
            }}>
              <span style={{ marginRight: '16px', fontSize: '24px', color: colors.accent.gold }}>◈</span>
              <span>New Calculation</span>
            </Link>
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <Link to="/admin" style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 20px',
                background: 'rgba(212, 175, 55, 0.1)',
                borderRadius: borderRadius.md,
                textDecoration: 'none',
                color: colors.accent.gold,
                border: `1px solid ${colors.border.gold}`,
                transition: 'all 0.3s ease',
              }}>
                <span style={{ marginRight: '16px', fontSize: '24px' }}>★</span>
                <span>Admin Dashboard</span>
              </Link>
            )}
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            background: 'rgba(248, 113, 113, 0.15)',
            color: colors.status.error,
            border: `1px solid ${colors.status.error}`,
            padding: '16px',
            borderRadius: borderRadius.md,
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.semibold,
            cursor: 'pointer',
            fontFamily: typography.fontFamily.body,
            transition: 'all 0.3s ease',
          }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default ProfilePage;
